import cx from 'classnames';
import React, { Component } from 'react';
import './React360.css';
import { isNonViewableAreaClicked } from '../../screens/NewProductUIV2/ShowCaseViewer/helpers';
import { isEmpty } from 'lodash';

// You can play with this to adjust the sensitivity
// higher values make mouse less sensitive
const pixelsPerDegree = 3;

class React360 extends Component {
  static defaultProps = { dir: 'awair-360', numImages: 55 };
  abortController = new AbortController(); // Create an instance of AbortController
  imageRef = React.createRef(null);
  dragOffset = React.createRef(null);
  state = {
    dragging: false,
    zoomDragging: false,
    imageIndex: 0,
    dragStartIndex: 0,
    list: [],
    baseList: [],
    zoomMode: false,
    zoomedImagePosition: { x: 0, y: 0 },
    zoomCenter: { x: 0, y: 0 },
    scale: 1,
  };

  componentDidMount = () => {
    document.addEventListener('mousemove', this.handleMouseMove, false);
    document.addEventListener('mouseup', this.handleMouseUp, false);
    document.addEventListener('touchmove', this.handleTouchMove, false);
    document.addEventListener('touchend', this.handleTouchEnd, false);
    this.createBlobURL();
  };

  componentDidUpdate = () => {
    if (!this.props?.imageZoomMode && this.state.zoomMode) {
      this.setState({ zoomMode: false });
    }
  };

  componentWillUnmount = () => {
    // Abort any ongoing fetch requests
    this.abortController.abort();

    document.removeEventListener('mousemove', this.handleMouseMove, false);
    document.removeEventListener('mouseup', this.handleMouseUp, false);
    document.removeEventListener('touchmove', this.handleTouchMove, false);
    document.removeEventListener('touchend', this.handleTouchEnd, false);
    this.state?.list?.forEach((blobUrl) => URL.revokeObjectURL(blobUrl));
    this.state?.baseList?.forEach((blobUrl) => URL.revokeObjectURL(blobUrl));
  };

  handleMouseDown = (e) => {
    e.persist();
    this.dragOffset = {
      x: e.clientX - this.state.zoomedImagePosition.x,
      y: e.clientY - this.state.zoomedImagePosition.y,
    }
    if (this.state.zoomMode) {
      this.setState({ zoomDragging: true });
    } else {
      this.setState((state) => ({
        dragging: true,
        dragStart: e.screenX,
        dragStartIndex: state.imageIndex,
      }));
    }
  };

  handleTouchStart = (e) => {
    e.persist();
    const touch = e.touches[0];
    this.dragOffset = {
      x: touch.clientX - this.state.zoomedImagePosition.x,
      y: touch.clientY - this.state.zoomedImagePosition.y,
    }
    if (this.state.zoomMode) {
      this.setState({ zoomDragging: true });
    } else {
      this.setState((state) => ({
        dragging: true,
        dragStart: touch.screenX,
        dragStartIndex: state.imageIndex,
      }));
    }
  };

  handleTouchMove = (e) => {
    if (this.state.zoomMode && this.state.zoomDragging) {
      const touch = e.touches[0];
      this.setState((state) => ({
        zoomedImagePosition: {
          x: touch.clientX - this.dragOffset.x,
          y: touch.clientY - this.dragOffset.y,
        },
      }));
    } else if (this.state.dragging) {
      const touch = e.touches[0];
      this.updateImageIndex(touch.screenX);
    }
  };

  handleZoomInOrOut = (event) => {
    event.preventDefault(); // Prevent default scroll behavior

    const scaleStep = 0.1; // Step size for zooming
    const minScale = 1; // Minimum scale limit
    const maxScale = 5; // Maximum scale limit

    const img = this.imageRef.current;
    const rect = img.getBoundingClientRect();

    let _scale = this.state.scale;

    if (event.deltaY < 0) {
      // Zoom in
      if (_scale >= maxScale) return;
      _scale = Math.min(this.state.scale + scaleStep, maxScale);
    } else {
      // Zoom out
      if (_scale <= minScale) return;
      _scale = Math.max(this.state.scale - scaleStep, minScale);
    }

    // Set transform origin dynamically
    img.style.transformOrigin = `${0}% ${0}%`;
    
    // Calculate new offsets to maintain zoom position
    const scaleRatio = _scale / this.state.scale;
    const newOffsetX = (event.clientX - rect.left) * (1 - scaleRatio);
    const newOffsetY = (event.clientY - rect.top) * (1 - scaleRatio);

    this.setState((state) => ({
      scale: _scale,
      zoomedImagePosition: {
        x: state.zoomedImagePosition.x + newOffsetX,
        y: state.zoomedImagePosition.y + newOffsetY,
      },
    }))
  };


  handleTouchEnd = () => {
    this.setState({ dragging: false, zoomDragging: false });
  };

  createBlobURL = async (e) => {
    const blobs = await Promise.all(
      this?.props?.productImages?.map((url) =>
        fetch(url, { signal: this.abortController.signal }).then((response) => response.blob())
      )
    );
    const blobUrls = blobs.map((blob) => URL.createObjectURL(blob));

    let baseBlobs = [];
    let baseBlobUrls = [];
    if (this?.props?.baseImages?.length > 0) {
      baseBlobs = await Promise.all(
        this?.props?.baseImages?.map((url) =>
          fetch(url, { signal: this.abortController.signal }).then((response) => response.blob())
        )
      );
      baseBlobUrls = baseBlobs.map((blob) => URL.createObjectURL(blob));
    }

    this.setState({
      ...this.state,
      list: blobUrls,
      baseList: baseBlobUrls,
    });
  };

  handleMouseUp = () => {
    this.setState({ dragging: false, zoomDragging: false });
  };

  updateImageIndex = (currentPosition) => {
    let numImages = this.props.numImages;
    const pixelsPerImage = pixelsPerDegree * (360 / numImages);
    const { dragStart, imageIndex, dragStartIndex } = this.state;
    // pixels moved
    let dx = (currentPosition - dragStart) / pixelsPerImage;
    let index = Math.floor(dx) % numImages;

    if (index < 0) {
      index = numImages + index - 1;
    }
    index = (index + dragStartIndex) % numImages;
    // console.log(index, dragStartIndex, numImages)
    if (index !== imageIndex) {
      window.$currentImageIndex = index;
      this.setState({ imageIndex: index });
    }
  };

  handleMouseMove = (e) => {
    if (this.state.zoomMode && this.state.zoomDragging) {
      const imageRect = this.imageRef.current.getBoundingClientRect()
      const rect = this.imageRef.current.parentNode.getBoundingClientRect()

      const {x:left, y:top} = this.state.zoomedImagePosition

      let x = e.clientX - this.dragOffset.x;
      let y = e.clientY - this.dragOffset.y;

      if (imageRect.left - rect.left + (x - left) > 0 || imageRect.right + (x - left) < rect.right) x = left
      if (imageRect.top - rect.top + (y - top) > 0 || imageRect.bottom + (y - top) < rect.bottom) y = top

      this.setState((state) => ({
        zoomedImagePosition: {
          x,
          y,
        },
      }));
    } else if (this.state.dragging) {
      this.updateImageIndex(e.screenX);
    }
  };

  preventDragHandler = (e) => {
    e.preventDefault();
  };

  handleDoubleClick = (e) => {
    if (!this.props?.isImageZoomEnabled) return;
    const { clientX, clientY } = e;
    const rect = e.target.getBoundingClientRect();

    // Compute relative mouse position inside the image
    const relativeX = (clientX - rect.left) / rect.width;
    const relativeY = (clientY - rect.top) / rect.height;

    // Compute transform-origin in percentages (for better control)
    const transformOriginX = relativeX * 100;
    const transformOriginY = relativeY * 100;

    const zoomCenter = {
      x: transformOriginX,
      y: transformOriginY,
    };

    // Adjust zoom position to account for the offset
    const zoomedImagePosition = this.state.zoomMode
      ? { x: 0, y: 0 }
      : {
        x: (e.target.naturalWidth / 2) - (e.target.naturalWidth * relativeX),
        y: (e.target.naturalHeight / 2) - (e.target.naturalHeight * relativeY)
      };
    this.setState((state) => ({
      zoomMode: !state.zoomMode,
      zoomedImagePosition,
      zoomCenter,
      zoomDragging: false,
      dragging: false,
      scale: 1,
    }));
    this.props?.setImageZoomMode?.(!this.state.zoomMode);
  };

  handleClick = (event) => {
    const isNonAreaClicked = isNonViewableAreaClicked({
      containerRef: this.props.containerRef,
      imageRef: this.imageRef,
      event,
    });
    if (isNonAreaClicked && isEmpty(this.props.imgContainerStyle)) {
      this.props?.onClose();
    }
  };

  renderImage = () => {
    const { imageIndex, zoomMode, zoomedImagePosition, zoomCenter } = this.state;
    return (
      <div
        className={cx('react360', this?.props?.imgContainerClassname, this.props.isFullscreen && zoomMode ? 'fullscreen' : '')}
        style={this.props.imgContainerStyle || {}}>
        {this?.props?.baseImages?.length > 0 ? (
          <img
            className={cx(
              'position-absolute',
              this?.props?.imgClassname,
              'react-360-img',
              this.props.isFullscreen ? 'fullscreen' : '',
              zoomMode ? 'zoom' : ''
            )}
            alt={`react-360-${imageIndex + 1}`}
            src={
              this.state.baseList && this.state.baseList?.length > 0
                ? this.state.baseList[imageIndex || 0]
                : this?.props?.baseImages[imageIndex || 0]
            }
            style={
              zoomMode
                ? {
                  ...(this?.props?.baseImgStyle || {}),
                  ...(this?.props?.imgStyle || {}),
                  top: `${zoomedImagePosition.y}px`,
                  left: `${zoomedImagePosition.x}px`,
                  transform: `scale(${this.state.scale})`,
                  transformOrigin: `0 0`,
                  zIndex: 1,
                }
                : {
                  ...(this?.props?.baseImgStyle || {}),
                  ...(this?.props?.imgStyle || {}),
                  zIndex: 1,
                }
            }
            loading="lazy"
          />
        ) : null}
        <img
          onMouseDown={this.handleMouseDown}
          onTouchStart={this.handleTouchStart}
          onWheel={this.handleZoomInOrOut}
          ref={this.imageRef}
          onLoad={(e) => {
            if (this.props.setImageLoaded) this.props.setImageLoaded(true);
          }}
          className={cx(
            this?.props?.imgClassname,
            'react-360-img position-relative',
            this.props.isFullscreen ? 'fullscreen' : '',
            zoomMode ? 'zoom' : ''
          )}
          alt={`react-360-${imageIndex + 1}`}
          src={
            this.state.list && this.state.list?.length > 0
              ? this.state.list[imageIndex || 0]
              : this?.props?.productImages[imageIndex || 0]
          }
          style={
            zoomMode
              ? {
                ...(this?.props?.imgStyle || {}),
                width: "fit-content",
                height: "fit-content",
                // top: `${zoomedImagePosition.y}px`,
                // left: `${zoomedImagePosition.x}px`,
                transform: `translate(${zoomedImagePosition.x}px, ${zoomedImagePosition.y}px) scale(${this.state.scale})`,
                transformOrigin: `0 0`,
                transition: "none",
                ...(this?.props?.baseImages?.length > 0 ? { zIndex: 2 } : {}),
              }
              : {
                ...(this?.props?.imgStyle || {}),
                ...(this?.props?.baseImages?.length > 0 ? { zIndex: 2 } : {}),
              }
          }
          loading="lazy"
        />
      </div>
    );
  };

  render = () => {
    return (
      <div
        className={`${this?.props?.className || ''} react-360-img`}
        onDragStart={this.preventDragHandler}
        onDoubleClick={this.handleDoubleClick}
        {...(this.props.enableClick && {
          onClick: this.handleClick,
        })}>
        {this.renderImage()}
      </div>
    );
  };
}

export default React360;
